import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { ProGetProQuery, ProGetProQueryVariables } from '@gql/graphql';
import { useMemo } from 'react';

export const PRO_GET_PRO = graphql(`
  query ProGetPro(
    $where: ProfessionalWhereInput!
    $serviceTranslationsWhere: ServiceTranslationWhereInput
    $categoryTranslationsWhere: CategoryTranslationWhereInput
    $proServiceLanguage: String!
  ) {
    searchProfessionals(take: 1, where: $where) {
      professionals {
        id
        type
        centerType
        email
        spokenLanguages
        centerType
        whatsappNumber
        phone
        language
        mobilePhone
        hasShop
        website
        visitAtHome
        selfDogWash
        parking
        acceptedAnimals
        onlineBookingEnabled
        acceptsOnlineAppointment
        visitAtHome
        paymentMethods
        employeesCount
        assistantsCount
        unclaimedProfile
        acceptsNewClients
        profileVideo
        profileVideoPos
        isTipawPartner
        proSchedule {
          dayOfWeek
          avails {
            start
            end
          }
        }
        shopSchedule {
          dayOfWeek
          openingHours {
            start
            end
          }
        }
        degrees {
          degree
          school
          dateEnd
        }
        imgs {
          id
          large
          medium
        }
        workers {
          id
          fullName
        }
        address {
          id
          formatted
          street
          streetNumber
          city
          zip
          lat
          lng
          country
        }
        urgencyAvailabilities {
          id
          dayOfWeek
        }
        services(language: $proServiceLanguage) {
          id
          fromDurationMinute
          toDurationMinute
          bookable
          priceType
          price
          priceFrom
          priceTo
          workerServices {
            id
            workerId
            minimumDuration
            maximumDuration
            bookingDuration
            animalSize
            animalType
            isProSpecialist
            translations {
              id
              language
              preBookingWarning
            }
          }
          service {
            id
            translations(where: $serviceTranslationsWhere) {
              title
            }
          }
          category {
            id
            translations(where: $categoryTranslationsWhere) {
              title
            }
          }
        }
        translations {
          id
          language
          url
          title
          description
          extraDescription
          subtitle
          wellnessEngagement
          preBookingWarning
          seoTitle
          seoDescription
          seoKeywords
        }
      }
    }
  }
`);

export const useGetPro = (url: string) => {
  const { language } = useRouter();

  const variables = useMemo((): ProGetProQueryVariables => {
    const translationsWhere = { language: { equals: language } };
    return {
      where: {
        published: { equals: true },
        OR: [
          {
            translations: {
              some: {
                AND: [{ url: { equals: url } }, { language: { equals: language } }],
              },
            },
          },
          { proMongoId: { equals: url } },
        ],
      },
      serviceTranslationsWhere: translationsWhere,
      categoryTranslationsWhere: translationsWhere,
      proServiceLanguage: language,
    };
  }, [url, language]);

  const { data, error, loading } = useQuery<ProGetProQuery, ProGetProQueryVariables>(PRO_GET_PRO, {
    variables,
    ssr: true,
  });

  return {
    pro: data?.searchProfessionals.professionals?.[0],
    proError: error,
    proLoading: loading,
  };
};
